import * as dicomParser from 'dicom-parser';

/** DICOM tag for Directory Record Sequence (0004,1220) */
const TAG_DIRECTORY_RECORD_SEQUENCE = 'x00041220';
/** DICOM tag for Lower Level Directory Record Sequence (0004,1420) */
const TAG_LOWER_LEVEL_DIRECTORY_RECORD = 'x00041420';

const TAG_STUDY_DATE = 'x00080020';
const TAG_STUDY_TIME = 'x00080030';
const TAG_STUDY_ID = 'x00200010';
const TAG_STUDY_INSTANCE_UID = 'x0020000d';
const TAG_STUDY_DESCRIPTION = 'x00081030';
const TAG_ACCESSION_NUMBER = 'x00080050';
const TAG_SERIES_NUMBER = 'x00200011';
const TAG_REFERENCED_FILE_ID = 'x00041500';

export interface StudyRecord {
  StudyDate?: string;
  StudyTime?: string;
  StudyID?: string;
  StudyInstanceUID?: string;
  StudyDescription?: string;
  AccessionNumber?: string;
}

export interface FileRecord {
  ReferencedFileID: string;
  StudyID?: string;
  SeriesNumber?: string;
  StudyDescription?: string;
  StudyInstanceUID?: string;
  AccessionNumber?: string;
}

export interface ParseDicomdirResult {
  studiesData: StudyRecord[];
  filesData: FileRecord[];
}

/**
 * Safely read a string from a dicom-parser dataset. Returns empty string if tag is missing or unreadable.
 */
function getString(dataSet: dicomParser.DataSet, tag: string): string {
  try {
    const value = dataSet.string(tag);
    return value != null ? String(value).trim() : '';
  } catch {
    return '';
  }
}

/**
 * Read ReferencedFileID; DICOM allows multiple path components. Returns path joined with '/'.
 */
function getReferencedFileId(dataSet: dicomParser.DataSet): string {
  try {
    const element = dataSet.elements[TAG_REFERENCED_FILE_ID];
    if (!element || element.length === 0) return '';
    const str = dataSet.string(TAG_REFERENCED_FILE_ID);
    if (str == null) return '';
    return String(str)
      .replace(/\\/g, '/')
      .replace(/\.\\/g, '/')
      .trim();
  } catch {
    return '';
  }
}

interface WalkContext {
  currentStudyID: string;
  currentSeriesNumber: string;
  currentStudyInstanceUID: string;
  currentStudyDescription: string;
  currentAccessionNumber: string;
}

function createContext(): WalkContext {
  return {
    currentStudyID: '',
    currentSeriesNumber: '',
    currentStudyInstanceUID: '',
    currentStudyDescription: '',
    currentAccessionNumber: '',
  };
}

function processRecord(
  dataSet: dicomParser.DataSet,
  context: WalkContext,
  studiesData: StudyRecord[],
  filesData: FileRecord[]
): void {
  const studyDate = getString(dataSet, TAG_STUDY_DATE);
  const studyTime = getString(dataSet, TAG_STUDY_TIME);
  const studyId = getString(dataSet, TAG_STUDY_ID);
  const studyInstanceUid = getString(dataSet, TAG_STUDY_INSTANCE_UID);
  const studyDescription = getString(dataSet, TAG_STUDY_DESCRIPTION);
  const accessionNumber = getString(dataSet, TAG_ACCESSION_NUMBER);
  const seriesNumber = getString(dataSet, TAG_SERIES_NUMBER);
  const referencedFileId = getReferencedFileId(dataSet);

  if (studyId) context.currentStudyID = studyId;
  if (seriesNumber) context.currentSeriesNumber = seriesNumber;
  if (studyInstanceUid) context.currentStudyInstanceUID = studyInstanceUid;
  if (studyDescription) context.currentStudyDescription = studyDescription;
  if (accessionNumber) context.currentAccessionNumber = accessionNumber;

  if (studyDate) {
    const studyRecord: StudyRecord = {};
    studyRecord.StudyDate = studyDate;
    if (studyTime) studyRecord.StudyTime = studyTime;
    if (studyId) studyRecord.StudyID = studyId;
    if (studyInstanceUid) studyRecord.StudyInstanceUID = studyInstanceUid;
    if (studyDescription) studyRecord.StudyDescription = studyDescription;
    if (accessionNumber) studyRecord.AccessionNumber = accessionNumber;
    studiesData.push(studyRecord);
  }

  if (referencedFileId) {
    filesData.push({
      ReferencedFileID: referencedFileId,
      StudyID: context.currentStudyID || undefined,
      SeriesNumber: context.currentSeriesNumber || undefined,
      StudyDescription: context.currentStudyDescription || undefined,
      StudyInstanceUID: context.currentStudyInstanceUID || undefined,
      AccessionNumber: context.currentAccessionNumber || undefined,
    });
  }

  const lowerLevel = dataSet.elements[TAG_LOWER_LEVEL_DIRECTORY_RECORD];
  if (lowerLevel && lowerLevel.items && Array.isArray(lowerLevel.items)) {
    for (const item of lowerLevel.items) {
      if (item && item.dataSet) {
        processRecord(item.dataSet, context, studiesData, filesData);
      }
    }
  }
}

/**
 * Parse a DICOMDIR file buffer in a single pass and return studiesData and filesData
 * in the shape expected by upload-helper (createUploadDICOMBlock, getDICOMDISKUploadBlock).
 */
export function parseDicomdirBuffer(buffer: ArrayBuffer): Promise<ParseDicomdirResult> {
  return new Promise((resolve, reject) => {
    try {
      const byteArray = new Uint8Array(buffer);
      const dataSet = dicomParser.parseDicom(byteArray);
      const dirSeq = dataSet.elements[TAG_DIRECTORY_RECORD_SEQUENCE];

      const studiesData: StudyRecord[] = [];
      const filesData: FileRecord[] = [];
      const context = createContext();

      if (!dirSeq || !dirSeq.items || !Array.isArray(dirSeq.items)) {
        resolve({ studiesData, filesData });
        return;
      }

      for (const item of dirSeq.items) {
        if (item && item.dataSet) {
          processRecord(item.dataSet, context, studiesData, filesData);
        }
      }

      resolve({ studiesData, filesData });
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
}
